/**
 * Public homeowner capture form — first-party consent.
 * Posts to /api/leads; offers JSON download if server cannot persist.
 */

const CONSENT_BLURB =
  "By submitting, you request info about patio shade / outdoor living and agree that " +
  "Bayway / LeadForge or a local licensed dealer may contact you by the methods you check " +
  "(call, text, and/or email). Msg & data rates may apply. Reply STOP to opt out of texts.";

export function renderCapture(_pack) {
  const params = new URLSearchParams(location.hash.split("?")[1] || "");
  const territory = params.get("territory") || "east_valley";
  const prefillAddr = params.get("address") || "";

  return `
    <div class="capture-wrap">
      <h1>Free outdoor living consult</h1>
      <p class="sub">Patio shade, pool screens &amp; outdoor rooms — a local specialist will follow up</p>
      <div class="panel capture-panel">
        <p class="muted">Takes about a minute. We only contact you the ways you check below — no spam lists.</p>
        <form id="capture-form" class="capture-form" novalidate>
          <input type="text" name="company_website" class="hp" tabindex="-1" autocomplete="off" aria-hidden="true" />
          <input type="hidden" name="source" value="web_capture" />
          <input type="hidden" name="territory" value="${escapeAttr(territory)}" />

          <div class="form-row two">
            <label>First name
              <input name="first_name" required autocomplete="given-name" />
            </label>
            <label>Last name
              <input name="last_name" required autocomplete="family-name" />
            </label>
          </div>
          <div class="form-row two">
            <label>Email
              <input name="email" type="email" autocomplete="email" placeholder="you@email.com" />
            </label>
            <label>Mobile phone
              <input name="phone" type="tel" autocomplete="tel" placeholder="(480) 555-0100" />
            </label>
          </div>
          <label>Street address
            <input name="address" required autocomplete="street-address" value="${escapeAttr(prefillAddr)}" />
          </label>
          <div class="form-row three">
            <label>City
              <input name="city" required autocomplete="address-level2" placeholder="Mesa" />
            </label>
            <label>State
              <input name="state" value="AZ" maxlength="2" autocomplete="address-level1" />
            </label>
            <label>ZIP
              <input name="zip" required autocomplete="postal-code" placeholder="85203" />
            </label>
          </div>
          <label>What are you interested in?
            <select name="interest">
              <option value="patio_shade">Patio shade / pergola</option>
              <option value="pool_screens">Pool screens / enclosure</option>
              <option value="full_outdoor_room">Full outdoor room</option>
              <option value="measure_only">Free on-site measure</option>
              <option value="not_sure">Not sure — advise me</option>
            </select>
          </label>
          <label>Preferred contact
            <select name="preferred_contact">
              <option value="text">Text</option>
              <option value="call">Call</option>
              <option value="email">Email</option>
              <option value="any">Any</option>
            </select>
          </label>
          <label>Notes (optional)
            <textarea name="notes" rows="2" placeholder="Pool? Existing patio cover? Best time to reach you?"></textarea>
          </label>

          <fieldset class="consent-box">
            <legend>How we may contact you</legend>
            <p class="muted small">${CONSENT_BLURB}</p>
            <label class="check-line"><input type="checkbox" name="consent_sms" /> Text me about this request</label>
            <label class="check-line"><input type="checkbox" name="consent_call" /> Call me about this request</label>
            <label class="check-line"><input type="checkbox" name="consent_email" checked /> Email me about this request</label>
          </fieldset>

          <button type="submit" class="btn primary" id="capture-submit">Request free design</button>
          <p id="capture-status" class="muted" role="status"></p>
        </form>
      </div>
      <p class="muted small">Consent version 2026-07-outdoor-v1 · We do not sell your info · Contractor-only fulfillment</p>
    </div>
  `;
}

function escapeAttr(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

export function mountCapture() {
  const form = document.getElementById("capture-form");
  if (!form) return;
  const status = document.getElementById("capture-status");
  const btn = document.getElementById("capture-submit");

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const fd = new FormData(form);
    const body = Object.fromEntries(fd.entries());
    body.consent_sms = form.querySelector('[name="consent_sms"]')?.checked === true;
    body.consent_call = form.querySelector('[name="consent_call"]')?.checked === true;
    body.consent_email = form.querySelector('[name="consent_email"]')?.checked === true;

    if (!body.consent_sms && !body.consent_call && !body.consent_email) {
      status.textContent = "Please check at least one contact method.";
      return;
    }
    if (!body.email && !body.phone) {
      status.textContent = "Email or phone is required.";
      return;
    }

    btn.disabled = true;
    status.textContent = "Sending…";
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        status.textContent = "Could not submit: " + (data.errors || [data.error || res.status]).join(", ");
        btn.disabled = false;
        return;
      }

      // Local backup for import if KV not bound
      try {
        const key = "leadforge_local_leads_v1";
        const list = JSON.parse(localStorage.getItem(key) || "[]");
        list.push(data.lead || { ...body, id: data.id, created_at: new Date().toISOString() });
        localStorage.setItem(key, JSON.stringify(list.slice(-200)));
      } catch {
        /* ignore */
      }

      form.reset();
      form.querySelector('[name="consent_email"]').checked = true;
      status.innerHTML =
        `<strong>Thanks!</strong> ${data.message || "We received your request."}` +
        (data.persisted === false
          ? ` <button type="button" class="btn" id="dl-lead">Download my submission</button>`
          : "");

      if (data.persisted === false && data.lead) {
        document.getElementById("dl-lead")?.addEventListener("click", () => {
          const blob = new Blob([JSON.stringify({ leads: [data.lead] }, null, 2)], {
            type: "application/json",
          });
          const a = document.createElement("a");
          a.href = URL.createObjectURL(blob);
          a.download = `leadforge-lead-${data.id || "submit"}.json`;
          a.click();
        });
      }
    } catch (err) {
      status.textContent = "Network error — try again or call your local dealer.";
      btn.disabled = false;
    }
  });
}
